import { useState } from "react";
import { Link } from "react-router-dom";
import { X, KeyRound, ArrowRight } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const SS_KEY = "wv:account-upsell:dismissed";

/** Banner that nudges legacy email-link visitors to create an account.
 *  Hidden when the user is already signed in. Per-session dismissable via
 *  sessionStorage so a single dismiss doesn't pester them on every page nav.
 *
 *  Drop this at the top of pages reached via `manage_token` URLs:
 *    /manage/<token>, /today/<token>, /hourly/<token>/<sub>, /share*.
 */
export default function AccountUpsell({ next = "/app", className = "" }) {
  const { user, isLoading } = useAuth();
  const [dismissed, setDismissed] = useState(() => {
    try { return sessionStorage.getItem(SS_KEY) === "1"; } catch { return false; }
  });

  if (isLoading) return null;
  if (user) return null;          // signed-in users don't see it
  if (dismissed) return null;

  const dismiss = () => {
    setDismissed(true);
    try { sessionStorage.setItem(SS_KEY, "1"); } catch {}
  };

  return (
    <div
      data-testid="account-upsell"
      className={`relative border-2 border-black bg-[#FFD600] text-black shadow-[3px_3px_0_#0A0A0A] ${className}`}
    >
      <div className="px-5 py-4 sm:px-6 sm:py-5 grid grid-cols-1 sm:grid-cols-[auto_1fr_auto] gap-4 items-center">
        <div className="hidden sm:flex w-12 h-12 bg-black items-center justify-center shrink-0">
          <KeyRound className="w-5 h-5 text-[#FFD600]" />
        </div>
        <div className="min-w-0">
          <div className="ticker-label !text-[10px] mb-1.5">Heads up</div>
          <div className="font-display text-lg sm:text-xl font-black tracking-tighter leading-tight">
            Create a Weathervane account &mdash; log in with Google, Apple, or just create a password.
          </div>
          <p className="text-xs sm:text-sm text-black/80 mt-1.5 leading-snug">
            Skip the email links. Manage everything in one place. Your existing forecasts and news come right along.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap sm:flex-nowrap">
          <Link
            to={`/login?next=${encodeURIComponent(next)}`}
            data-testid="account-upsell-signin"
            className="inline-flex items-center gap-1 px-3.5 py-2 border-2 border-black bg-white hover:bg-[#FFFBE6] text-[10px] uppercase tracking-[0.18em] font-bold whitespace-nowrap"
          >
            Sign in
          </Link>
          <Link
            to={`/signup?next=${encodeURIComponent(next)}`}
            data-testid="account-upsell-signup"
            className="inline-flex items-center gap-1.5 px-3.5 py-2 border-2 border-black bg-black text-[#FFD600] hover:bg-white hover:text-black text-[10px] uppercase tracking-[0.18em] font-bold whitespace-nowrap"
          >
            Create account <ArrowRight className="w-3 h-3" />
          </Link>
        </div>
      </div>
      <button
        onClick={dismiss}
        data-testid="account-upsell-dismiss"
        aria-label="Dismiss"
        className="absolute top-1.5 right-1.5 p-1 hover:bg-black/10"
      >
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}
